import _ from "lodash";
import mario from "@images/sandbox/mario.png";
import sonic from "@images/sandbox/sonic.png";
import knuckles from "@images/sandbox/knuckles.png";
import peach from "@images/sandbox/peach.png";
import sailormoon from "@images/sandbox/sailormoon.png";
import megaman from "@images/sandbox/megaman.png";
import samus from "@images/sandbox/samus.png";
import { ILineup, LINEUP_POSITIONS, LineupOption, LineupOptionID, LineupScoreDetail } from "../types";

const IMGS = [
  mario,
  sonic,
  knuckles,
  peach,
  sailormoon,
  megaman,
  samus,
];

const TEAMS = [
  '1',
  '2',
  '3',
  '4',
  '6',
  '7',
  '8',
  '9',
  '11',
  '12',
  '13',
  '14',
  '15',
  '44',
  '62',
];

const ROUND_ID = 14;

const ALL_SCORE_DETAILS: LineupScoreDetail[] = [
  { key: "goals", amount: 1, score: 1 },
  { key: "gw_goals", amount: 1, score: 1 },
  { key: "assists", amount: 1, score: 1 },
  { key: "sh_goals", amount: 1, score: 1 },
  { key: "shots_on_goal", amount: 1, score: 1 },
  { key: "blocked_shots", amount: 1, score: 1 },
  { key: "penalty_minutes", amount: 1, score: 1 },
  { key: "positive", amount: 1, score: 1 },
  { key: "negative", amount: 1, score: 1 },
  { key: "win", amount: 1, score: 1 },
  { key: "ot_win", amount: 1, score: 1 },
  { key: "ot_lose", amount: 1, score: 1 },
  { key: "goals_against", amount: 1, score: 1 },
  { key: "saves", amount: 1, score: 1 },
  { key: "shutout", amount: 1, score: 1 },
];

const SOME_SCORE_DETAILS: LineupScoreDetail[] = [
  { key: "goals", amount: 1, score: 1 },
  { key: "gw_goals", amount: 1, score: 1 },
  { key: "assists", amount: 1, score: 1 },
  { key: "sh_goals", amount: 1, score: 1 }
];

export const MOCKED_OPTIONS_VERSION = '___mocked-options-version';
const BUDGET_FULL = 60;
const NOW_IN_2_DAYS = (() => {
  const now = new Date();
  now.setDate(now.getDate() + 2);
  return now.toISOString();  
})();

const LINEUP_DEFAULT: ILineup = {
  id: '___mock-lineup-od',
  budget_left: BUDGET_FULL,
  total_budget: BUDGET_FULL,
  open_until: NOW_IN_2_DAYS,
  countdown_sec: 99999,
  restrictions: {
    max_without_german_license: 9,
  },
  phase: 'open',
  slots: {},
  validity: {
    valid: false,
  },
  round_id: ROUND_ID
};

const mockOption = (id: LineupOptionID): LineupOption => {
  return {
    german_license: false,
    country_abbr: 'Can',
    id,
    image_url: '',
    name: `Name ${id} Surname`,
    position: 'def',
    price: 3,
    team: 'team1',
    short_name: `Player${id}`,
    active: true,
    stat_perc_picked: 42.5,
    stat_prev_score: 24,
    stat_score_sum: 42,
  };
}

const OPTIONS_DEFAULT: LineupOption[] = [
  ..._.times(200).map((num) => ({
    ...mockOption(`${num}`),
    image_url: IMGS[num % IMGS.length],
    team: TEAMS[num % TEAMS.length],
    german_license: num % 10 > 3,
    position: LINEUP_POSITIONS[num % LINEUP_POSITIONS.length],
    active: num % 5 !== 0,
    ...(
      num % 13 === 0
        ? {
          name: mockOption(`${num}`).name + " Longname here!",
          short_name: undefined,
        }
        : {})
  })),
];

const STAT_SCORE_SUMS = [42, 0, -42];
const STAT_PREV_SCORES = [42.5, 0, -42.5];

export interface Scenario {
  lineup: ILineup;
  options: LineupOption[];
}

const SOME_SLOTS: ILineup['slots'] = {
  goal: { pick: OPTIONS_DEFAULT[0].id, bought_for_budget: OPTIONS_DEFAULT[0].price + 1},
  goalsub: { pick: OPTIONS_DEFAULT[1].id, bought_for_budget: OPTIONS_DEFAULT[1].price},
  def1: { pick: OPTIONS_DEFAULT[2].id, bought_for_budget: OPTIONS_DEFAULT[2].price + 1},
  def2: { pick: OPTIONS_DEFAULT[3].id, bought_for_budget: OPTIONS_DEFAULT[3].price},
  for4: { pick: OPTIONS_DEFAULT[4].id, bought_for_budget: OPTIONS_DEFAULT[4].price - 1},
};

export const mockedScenarios = [
  { name: 'initial',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'pickable',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        slots: {
          ...LINEUP_DEFAULT.slots,
          ...SOME_SLOTS,
        },
        edit_info: {
          can_edit: true,
        },
        validity: {
          valid: false,
          invalid_reason: 'missing_players'
        }
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'censored',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        censored: true,
        from_other: true,
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'negative budget',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        budget_left: -4,
        slots: {
          ...SOME_SLOTS,
        },
        validity: {
          valid: false,
          invalid_reason: 'budget_exceeded',
          is_critical: true,
          mark_invalid_slots: ['def1', 'def2']
        }
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'valid status (though slots are empty...)',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        validity: {
          valid: true,
        }
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'Live and scores',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        phase: 'closed',
        countdown_sec: 0,
        score_info: {
          total_score: 99,
        },
        slots: _.fromPairs(_.map(_.toPairs(SOME_SLOTS), ([key, value]) => {
          return [key, {
            ...value,
            score_info: {
              part_of_scoring: key === 'goalsub' ? false : true,
              score: 3,
              score_details: key === 'goalsub' ? [] : ALL_SCORE_DETAILS,
            }
          }];
        })),
        validity: {
          valid: true,
        }
      },
      options: OPTIONS_DEFAULT,
    }
  },
  { name: 'Completely scored',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        phase: 'scored',
        countdown_sec: 0,
        score_info: {
          total_score: 99,
        },
        slots: _.fromPairs(_.map(_.toPairs(SOME_SLOTS), ([key, value]) => {
          return [key, {
            ...value,
            score_info: {
              part_of_scoring: true,
              score: 5,
              score_details: key === 'goalsub' ? [] : ALL_SCORE_DETAILS,
            }
          }];
        })),
        validity: {
          valid: true,
        }
      },
      options: OPTIONS_DEFAULT,
    }
  },
  {
    name: 'with negative scores',
    scenario: {
      lineup: {
        ...LINEUP_DEFAULT,
        phase: 'open',
        countdown_sec: 0,
        score_info: {
          total_score: 99,
        },
        edit_info: {
          can_edit: true,
        },
        slots: {
          for1: {
            pick: '3',
            bought_for_budget: 3,
            score_info: {
              part_of_scoring: true,
              score: 5,
              score_details: ALL_SCORE_DETAILS,
            },
          },
          for2: {
            pick: '4',
            bought_for_budget: 3,
            score_info: {
              part_of_scoring: true,
              score: 5,
              score_details: SOME_SCORE_DETAILS,
            },
          },
          for3: {
            pick: '8',
            bought_for_budget: 3,
            score_info: {
              part_of_scoring: true,
              score: 5,
              score_details: SOME_SCORE_DETAILS,
            },
          },
        },
        validity: {
          valid: true,
        },
      },
      options: OPTIONS_DEFAULT.map((option, index) => ({
        ...option,
        stat_prev_score: STAT_PREV_SCORES[index % 3],
        stat_score_sum: STAT_SCORE_SUMS[index % 3],
      })),
    }
  }
] as const satisfies {name: string, scenario: Scenario}[];

export type ScenarioName = (typeof mockedScenarios)[number]['name'];

export const mockedScenario = (name: ScenarioName): Scenario => {
  return mockedScenarios.find((s) => s.name === name)!.scenario
}

export const scenarioNames = () => _.map(mockedScenarios, (s) => s.name);
